/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {formatDate} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {DeletePopUpComponent} from '../../../core/components/pop-ups/delete-pop-up/delete-pop-up.component';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {ColumnHeader} from '../../../core/models/column-header.model';
import {WorksheetExportRequest} from '../../../core/models/worksheet-export-request.model';
import {AllScenariosWithAssociationsDTO} from '../../../models/all-scenarios-with-associations-dto.model';
import {IndustryScenarioWithAssociationsDTO} from '../../../models/industry-scenario-with-associations-dto.model';
import {PropulsionScenarioWithAssociationsDTO} from '../../../models/propulsion-scenario-with-associations-dto.model';
import {VehicleScenarioWithAssociationsDTO} from '../../../models/vehicle-scenario-with-associations-dto.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {IndustryScenarioService} from '../../../services/industry-scenario.service';
import {
  ManageAllScenariosWithAssociationsService
} from '../../../services/manage-all-scenarios-with-associations-service';
import {PropulsionScenarioService} from '../../../services/propulsion-scenario-service';
import {VehicleScenarioService} from '../../../services/vehicle-scenario.service';

export const ALL_FCORGS = 'ALL FCORGS';
export const ALL_SCENARIOS = 'all scenarios';
export const EXCEL_EXPORT_ALL_SCENARIOS = 'All Scenarios';
export const EXCEL_EXPORT_VEH_SCENARIOS = 'Vehicle Scenarios';
export const EXCEL_EXPORT_PROP_SCENARIOS = 'Propulsion Scenarios';
export const EXCEL_EXPORT_IND_SCENARIOS = 'Industry Scenarios';
export const VEHICLE = 'vehicle';
export const INDUSTRY = 'industry';
export const PROPULSION = 'propulsion';
export const GLBL_SCEN_ID = 'GLBL SCEN ID';
export const GLBL_SCEN_NAME = 'GLBL SCEN NAME';
export const VEH_SCEN_ID = 'VEH SCEN ID';
export const VEH_SCEN_NAME = 'VEH SCEN NAME';
export const CREATED_BY_ID = 'CREATED BY ID';

@Component({
    selector: 'app-manage-scenarios',
    templateUrl: './manage-scenarios.component.html',
    styleUrls: ['./manage-scenarios.component.scss'],
    standalone: false
})
export class ManageScenariosComponent implements OnInit {
  showSpinner: boolean;
  noData: boolean;

  pageSize = 20;
  currentScenarioType: string;

  allScenariosHeaders: ColumnHeader[] = [
    {name: 'count', label: '#'},
    {name: 'globalScenarioId', label: GLBL_SCEN_ID},
    {name: 'globalScenarioName', label: GLBL_SCEN_NAME},
    {name: 'fcorg', label: 'FCORG'},
    {name: 'vehicleScenarioId', label: VEH_SCEN_ID},
    {name: 'vehicleScenarioName', label: VEH_SCEN_NAME},
    {name: 'vehicleScenarioType', label: 'SCEN TYPE'},
    {name: 'vehicleScenarioCategory', label: 'SCEN CAT'},
    {name: 'vehicleScenarioAssumption', label: 'VEH SCEN ASMPTN'},
    {name: 'vehicleScenarioCreatedById', label: 'CREATED BY ID'},
    {name: 'industryScenarioId', label: 'INDSTR SCEN ID'},
    {name: 'industryScenarioName', label: 'INDSTR SCEN NAME'},
    {name: 'industryScenarioType', label: 'SCEN TYPE'},
    {name: 'industryScenarioCategory', label: 'SCEN CAT'},
    {name: 'industryScenarioAssumption', label: 'INDSTR SCEN ASMPTN'},
    {name: 'propulsionScenarioId', label: 'PROP SCEN ID'},
    {name: 'propulsionScenarioName', label: 'PROP SCEN NAME'},
    {name: 'propulsionScenarioCreatedById', label: CREATED_BY_ID}];

  allScenariosColDef: ColDef[] = [
    {field: 'count', headerName: '#'},
    {field: 'globalScenarioId', headerName: GLBL_SCEN_ID, filter: AutocompleteFilterComponent},
    {field: 'globalScenarioName', headerName: GLBL_SCEN_NAME, filter: AutocompleteFilterComponent},
    {field: 'fcorg', headerName: 'FCORG', filter: AutocompleteFilterComponent},
    {field: 'vehicleScenarioId', headerName: VEH_SCEN_ID, filter: AutocompleteFilterComponent},
    {field: 'vehicleScenarioName', headerName: VEH_SCEN_NAME, filter: AutocompleteFilterComponent},
    {field: 'vehicleScenarioType', headerName: 'SCEN TYPE', filter: AutocompleteFilterComponent},
    {field: 'vehicleScenarioCategory', headerName: 'SCEN CAT', filter: AutocompleteFilterComponent},
    {field: 'vehicleScenarioAssumption', headerName: 'VEH SCEN ASMPTN', filter: AutocompleteFilterComponent},
    {field: 'vehicleScenarioCreatedById', headerName: 'CREATED BY ID', filter: AutocompleteFilterComponent},
    {field: 'industryScenarioId', headerName: 'INDSTR SCEN ID', filter: AutocompleteFilterComponent},
    {field: 'industryScenarioName', headerName: 'INDSTR SCEN NAME', filter: AutocompleteFilterComponent},
    {field: 'industryScenarioType', headerName: 'SCEN TYPE', filter: AutocompleteFilterComponent},
    {field: 'industryScenarioCategory', headerName: 'SCEN CAT', filter: AutocompleteFilterComponent},
    {field: 'industryScenarioAssumption', headerName: 'INDSTR SCEN ASMPTN', filter: AutocompleteFilterComponent},
    {field: 'propulsionScenarioId', headerName: 'PROP SCEN ID', filter: AutocompleteFilterComponent},
    {field: 'propulsionScenarioName', headerName: 'PROP SCEN NAME', filter: AutocompleteFilterComponent},
    {field: 'propulsionScenarioCreatedById', headerName: CREATED_BY_ID, filter: AutocompleteFilterComponent}];

  vehicleScenariosHeaders: ColumnHeader[] = [
    {name: 'count', label: '#'},
    {name: 'globalScenarioId', label: GLBL_SCEN_ID},
    {name: 'globalScenarioName', label: GLBL_SCEN_NAME},
    {name: 'fcorg', label: 'FCORG'},
    {name: 'vehicleScenarioId', label: VEH_SCEN_ID},
    {name: 'vehicleScenarioName', label: VEH_SCEN_NAME},
    {name: 'scenarioType', label: 'SCEN TYPE'},
    {name: 'scenarioCategory', label: 'SCEN CAT'},
    {name: 'vehicleScenarioAssumption', label: 'VEH SCEN ASMPTN'},
    {name: 'createdById', label: CREATED_BY_ID},
    {name: 'isEditLocked', label: 'EDIT LOCK'},
    {name: 'isDeleteLocked', label: 'DEL LOCK'}];

  vehicleScenariosColDef: ColDef[] = [
    {field: 'count', headerName: '#'},
    {field: 'globalScenarioId', headerName: 'GLBL SCEN ID', filter: AutocompleteFilterComponent},
    {field: 'globalScenarioName', headerName: 'GLBL SCEN NAME', filter: AutocompleteFilterComponent},
    {field: 'fcorg', headerName: 'FCORG', filter: AutocompleteFilterComponent},
    {field: 'vehicleScenarioId', headerName: 'VEH SCEN ID', filter: AutocompleteFilterComponent},
    {field: 'vehicleScenarioName', headerName: 'VEH SCEN NAME', filter: AutocompleteFilterComponent},
    {field: 'scenarioType', headerName: 'SCEN TYPE', filter: AutocompleteFilterComponent},
    {field: 'scenarioCategory', headerName: 'SCEN CAT', filter: AutocompleteFilterComponent},
    {field: 'vehicleScenarioAssumption', headerName: 'VEH SCEN ASMPTN', filter: AutocompleteFilterComponent},
    {field: 'createdById', headerName: 'CREATED BY ID', filter: AutocompleteFilterComponent},
    {field: 'isEditLocked', headerName: 'EDIT LOCK'},
    {field: 'isDeleteLocked', headerName: 'DEL LOCK'}
  ];

  propulsionScenariosHeaders: ColumnHeader[] = [
    {name: 'count', label: '#'},
    {name: 'globalScenarioId', label: GLBL_SCEN_ID},
    {name: 'globalScenarioName', label: GLBL_SCEN_NAME},
    {name: 'fcorg', label: 'FCORG'},
    {name: 'propulsionScenarioId', label: 'PROP SCEN ID'},
    {name: 'propulsionScenarioName', label: 'PROP SCEN NAME'},
    {name: 'createdById', label: CREATED_BY_ID},
    {name: 'isEditLocked', label: 'EDIT LOCK'},
    {name: 'isDeleteLocked', label: 'DEL LOCK'}];

  propulsionScenariosColDef: ColDef[] = [
    {field: 'count', headerName: '#'},
    {field: 'globalScenarioId', headerName: 'GLBL SCEN ID', filter: AutocompleteFilterComponent},
    {field: 'globalScenarioName', headerName: 'GLBL SCEN NAME', filter: AutocompleteFilterComponent},
    {field: 'fcorg', headerName: 'FCORG', filter: AutocompleteFilterComponent},
    {field: 'propulsionScenarioId', headerName: 'PROP SCEN ID', filter: AutocompleteFilterComponent},
    {field: 'propulsionScenarioName', headerName: 'PROP SCEN NAME', filter: AutocompleteFilterComponent},
    {field: 'createdById', headerName: 'CREATED BY ID', filter: AutocompleteFilterComponent},
    {field: 'isEditLocked', headerName: 'EDIT LOCK'},
    {field: 'isDeleteLocked', headerName: 'DEL LOCK'}
  ];

  industryScenariosHeaders: ColumnHeader[] = [
    {name: 'count', label: '#'},
    {name: 'vehicleScenarioId', label: VEH_SCEN_ID},
    {name: 'vehicleScenarioName', label: VEH_SCEN_NAME},
    {name: 'fcorg', label: 'FCORG'},
    {name: 'industryScenarioId', label: 'INDSTR SCEN ID'},
    {name: 'industryScenarioName', label: 'INDSTR SCEN NAME'},
    {name: 'industryScenarioType', label: 'SCEN TYPE'},
    {name: 'industryScenarioCategory', label: 'SCEN CAT'},
    {name: 'industryScenarioAssumption', label: 'INDSTR SCEN ASMPTN'},
    {name: 'createdById', label: CREATED_BY_ID},
    {name: 'isEditLocked', label: 'EDIT LOCK'},
    {name: 'isDeleteLocked', label: 'DEL LOCK'}];

  industryScenariosColDef: ColDef[] = [
    {field: 'count', headerName: '#'},
    {field: 'vehicleScenarioId', headerName: 'VEH SCEN ID', filter: AutocompleteFilterComponent},
    {field: 'vehicleScenarioName', headerName: 'VEH SCEN NAME', filter: AutocompleteFilterComponent},
    {field: 'fcorg', headerName: 'FCORG', filter: AutocompleteFilterComponent},
    {field: 'industryScenarioId', headerName: 'INDSTR SCEN ID', filter: AutocompleteFilterComponent},
    {field: 'industryScenarioName', headerName: 'INDSTR SCEN NAME', filter: AutocompleteFilterComponent},
    {field: 'industryScenarioType', headerName: 'SCEN TYPE', filter: AutocompleteFilterComponent},
    {field: 'industryScenarioCategory', headerName: 'SCEN CAT', filter: AutocompleteFilterComponent},
    {field: 'industryScenarioAssumption', headerName: 'INDSTR SCEN ASMPTN', filter: AutocompleteFilterComponent},
    {field: 'createdById', headerName: 'CREATED BY ID', filter: AutocompleteFilterComponent},
    {field: 'isEditLocked', headerName: 'EDIT LOCK'},
    {field: 'isDeleteLocked', headerName: 'DEL LOCK'}
  ];

  allScenariosList: AllScenariosWithAssociationsDTO[] = [];
  vehicleScenarios: VehicleScenarioWithAssociationsDTO[] = [];
  propulsionScenarios: PropulsionScenarioWithAssociationsDTO[] = [];
  industryScenarios: IndustryScenarioWithAssociationsDTO[] = [];

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  rowSelection: RowSelectionOptions = {
    mode: 'multiRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
    headerCheckbox: false
  };

  selectedScenarios: any[] = [];

  worksheetExportRequest: WorksheetExportRequest;


  constructor(public snackBar: FabMessageService,
              private readonly manageAllScenariosWithAssociationsService: ManageAllScenariosWithAssociationsService,
              private readonly vehicleScenarioService: VehicleScenarioService,
              private readonly propulsionScenarioService: PropulsionScenarioService,
              private readonly industryScenarioService: IndustryScenarioService,
              private readonly dialog: MatDialog,
              public readonly excelService: ExcelExportService) {
  }

  /**
   * Sets current scenario type to all scenarios
   * Sets displayed columns from headers for all scenarios
   * Load all scenarios
   * Sets variables for paginator
   */
  ngOnInit(): void {
    this.currentScenarioType = ALL_SCENARIOS;
    this.loadAllScenariosWithAssociations();
    this.loadAllVehicleScenariosWithAssociations();
    this.loadAllPropulsionScenariosWithAssociations();
    this.loadAllIndustryScenariosWithAssociations();
    this.resetTableState();
  }

  /**
   *  Sets initial paginator values for variables on init
   */
  resetTableState(): void {
    this.selectedScenarios = [];
    this.pageSize = 20;
  }

  /**
   * Fetch all scenarios with associations from backend endpoint
   */
  loadAllScenariosWithAssociations(): void {
    this.showSpinner = true;
    this.manageAllScenariosWithAssociationsService.getAllScenariosWithAssociations().subscribe(
      data => {
        this.allScenariosList = data;
      },
      err => {
        this.errorHandle(err);
      },
      () => {
        this.allScenariosList.forEach((scenario, index) => scenario.count = index + 1);
        this.setExportRequest(EXCEL_EXPORT_ALL_SCENARIOS, this.allScenariosList,
          this.allScenariosHeaders);
        this.showSpinner = false;
        this.noData = (this.allScenariosList.length === 0);
      }
    );
  }

  /**
   * Fetch vehicle scenarios with associations from backend endpoint
   */
  loadAllVehicleScenariosWithAssociations(): void {
    this.vehicleScenarioService.getAllVehicleScenariosWithAssociations().subscribe(
      data => {
        this.vehicleScenarios = data;
      },
      err => {
        this.errorHandle(err);
      },
      () => {
        this.vehicleScenarios.forEach((scenario, index) => scenario.count = index + 1);
        this.setExportRequest(EXCEL_EXPORT_VEH_SCENARIOS, this.vehicleScenarios,
          this.vehicleScenariosHeaders);
        this.noData = (this.vehicleScenarios.length === 0);
      }
    );
  }

  /**
   * Fetch propulsion scenarios with associations from backend endpoint
   */
  loadAllPropulsionScenariosWithAssociations(): void {
    this.propulsionScenarioService.getPropulsionScenariosWithAssociations().subscribe(
      data => {
        this.propulsionScenarios = data;
      },
      err => {
        this.errorHandle(err);
      },
      () => {
        this.propulsionScenarios.forEach((scenario, index) => scenario.count = index + 1);
        this.setExportRequest(EXCEL_EXPORT_PROP_SCENARIOS, this.propulsionScenarios,
          this.propulsionScenariosHeaders);
        this.noData = (this.propulsionScenarios.length === 0);
      }
    );
  }

  /**
   * Fetch industry scenarios with associations from backend endpoint
   */
  loadAllIndustryScenariosWithAssociations(): void {
    this.industryScenarioService.getIndustryScenariosWithAssociations().subscribe(
      data => {
        this.industryScenarios = data;
      },
      err => {
        this.errorHandle(err);
      },
      () => {
        this.industryScenarios.forEach((scenario, index) => scenario.count = index + 1);
        this.setExportRequest(EXCEL_EXPORT_IND_SCENARIOS, this.industryScenarios,
          this.industryScenariosHeaders);
        this.noData = (this.industryScenarios.length === 0);
      }
    );
  }

  /**
   * Sets the scenario as selected
   * @param event the clicked scenario row
   */
  onSelect(event) {
    if (event.node.selected) {
      this.selectedScenarios.push(event.data);
    } else {
      this.selectedScenarios = this.selectedScenarios.filter(element => element != event.data);
    }
  }

  /**
   * On click delete button, call the appropriate delete validation method
   */
  onClickDelete(): void {
    if (this.currentScenarioType === INDUSTRY) {
      this.deleteIndustryScenariosList();
    }
    if (this.currentScenarioType === PROPULSION) {
      this.deletePropulsionScenarioValidation();
    }
    if (this.currentScenarioType === VEHICLE) {
      this.deleteVehicleScenariosList();
    }
  }

  /**
   * Validation for vehicle scenario delete
   * Call the delete deleteSelectedScenarios method with the valid scenarios to be deleted
   */
  deleteVehicleScenariosList(): void {
    const invalidIds: number[] = [];
    const vehicleScenarioIdsToBeDeleted: number[] = [];
    for (const vehicleScenario of this.selectedScenarios) {
      if (vehicleScenario.globalScenarioId != null || vehicleScenario.isDeleteLocked === true) {
        invalidIds.push(vehicleScenario.vehicleScenarioId);
      } else {
        vehicleScenarioIdsToBeDeleted.push(vehicleScenario.vehicleScenarioId);
      }
    }
    const invalidIdsString = invalidIds.join();
    if (invalidIds.length > 0) {
      const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
        width: '400px',
        data: {
          title: 'Confirmation',
          message: `Vehicle scenario(s) with ID(s): ${invalidIdsString}
          cannot be deleted because they are associated with a global scenario or they are locked.`,
          yesLabel: 'Ok',
          actionColor: 'primary'
        },
        disableClose: true
      });
      dialogRef.afterClosed().subscribe({
        error: err => {
          this.errorHandle(err);
        },
        complete: () => {
          this.deleteSelectedScenarios(vehicleScenarioIdsToBeDeleted, false);
        }
      });
    } else {
      this.deleteSelectedScenarios(vehicleScenarioIdsToBeDeleted, false);
    }
  }

  /**
   * Validation for propulsion scenario delete
   * Create the invalidPropulsionScenarioIds list
   * Create the propulsionScenarioIdsToBeDeleted list
   * Create the attachedToVehicleScenario list
   * Call the deletePropulsionScenariosList method with the propulsion scenario lists
   */
  deletePropulsionScenarioValidation(): void {
    let invalidPropulsionScenarioIds: number[] = [];
    let attachedToVehicleScenario: number[] = [];
    let propulsionScenarioIdsToBeDeleted: number[] = [];

    for (const propulsionScenario of this.selectedScenarios) {
      const isAttachedToGlobalScen = this.propulsionScenarios
      .find(scenario => scenario.globalScenarioId != null
        && scenario.propulsionScenarioId === propulsionScenario.propulsionScenarioId) !== undefined;
      // if the scenario is attached to a global scenario or is deleted locked
      // add the scenario id to invalidPropulsionScenarioIds
      if (isAttachedToGlobalScen || propulsionScenario.isDeleteLocked) {
        invalidPropulsionScenarioIds.push(propulsionScenario.propulsionScenarioId);
        invalidPropulsionScenarioIds = invalidPropulsionScenarioIds.filter((value, index) =>
          invalidPropulsionScenarioIds.indexOf(value) === index);

      } else {
        // if the scenario is attached to a vehicle scenario
        // add the scenario id to attachedToVehicleScenario
        if (propulsionScenario.vehicleScenarioId != null) {
          attachedToVehicleScenario.push(propulsionScenario.propulsionScenarioId);
          attachedToVehicleScenario = attachedToVehicleScenario.filter((value, index) =>
            attachedToVehicleScenario.indexOf(value) === index);
        } else {
          // if the scenario is not attached to a vehicle scenario
          // add the scenario id to propulsionScenarioIdsToBeDeleted
          propulsionScenarioIdsToBeDeleted.push(propulsionScenario.propulsionScenarioId);
          propulsionScenarioIdsToBeDeleted = propulsionScenarioIdsToBeDeleted
          .filter((value, index) => propulsionScenarioIdsToBeDeleted
          .indexOf(value) === index);
        }
      }
    }
    this.deletePropulsionScenariosList(invalidPropulsionScenarioIds, propulsionScenarioIdsToBeDeleted,
      attachedToVehicleScenario);
  }

  /**
   * Call the delete deleteSelectedScenarios method with the valid scenarios to be deleted
   * @param invalidPropulsionScenarioIds Propulsion scenarios that are attached to a global scenario or delete locked
   * @param propulsionScenarioIdsToBeDeleted Propulsion scenarios that valid to be deleted
   * @param attachedToVehicleScenario Propulsion scenarios that need to be derefrenced from a
   * vehicle scenario and then deleted
   */
  deletePropulsionScenariosList(invalidPropulsionScenarioIds: number[], propulsionScenarioIdsToBeDeleted: number[],
                                attachedToVehicleScenario: number[]): void {
    const invalidIdsString = invalidPropulsionScenarioIds.join();
    if (invalidPropulsionScenarioIds.length > 0) {
      const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
        width: '400px',
        data: {
          title: 'Confirmation',
          message: `Propulsion scenario(s) with ID(s): ${invalidIdsString}
          cannot be deleted because they are associated with a global scenario or they are locked.`,
          yesLabel: 'Ok',
          actionColor: 'primary'
        },
        disableClose: true
      });
      dialogRef.afterClosed().subscribe({
        error: err => {
          this.errorHandle(err);
        },
        complete: () => {
          if (propulsionScenarioIdsToBeDeleted.length > 0) {
            // call deleteSelectedScenarios with the list of ids, and false for isAttachedToVehicle
            this.deleteSelectedScenarios(propulsionScenarioIdsToBeDeleted, false);
          }
          // call deleteSelectedScenarios with the list of ids, and true for isAttachedToVehicle so the propulsion
          // scenario can be removed from the vehicle scenario
          if (attachedToVehicleScenario.length > 0) {
            this.deleteSelectedScenarios(attachedToVehicleScenario, true);
          }
        }
      });
    } else {
      if (propulsionScenarioIdsToBeDeleted.length > 0) {
        this.deleteSelectedScenarios(propulsionScenarioIdsToBeDeleted, false);
      } else if (attachedToVehicleScenario.length > 0) {
        this.deleteSelectedScenarios(attachedToVehicleScenario, true);
      }
    }
  }

  /**
   * Validation for industry scenario delete
   * Call the delete deleteSelectedScenarios method with the valid scenarios to be deleted
   */
  deleteIndustryScenariosList(): void {
    const invalidIds: number[] = [];
    const industryScenarioIdsToBeDeleted: number[] = [];
    for (const industryScenario of this.selectedScenarios) {
      if (industryScenario.vehicleScenarioId != null || industryScenario.isDeleteLocked === true) {
        invalidIds.push(industryScenario.industryScenarioId);
      } else {
        industryScenarioIdsToBeDeleted.push(industryScenario.industryScenarioId);
      }
    }
    const invalidIdsString = invalidIds.join();
    if (invalidIds.length > 0) {
      const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
        width: '400px',
        data: {
          title: 'Confirmation',
          message: `Industry scenario(s) with ID(s): ${invalidIdsString}
          cannot be deleted because they are associated with a vehicle scenario or they are locked.`,
          yesLabel: 'Ok',
          actionColor: 'primary'
        },
        disableClose: true
      });
      dialogRef.afterClosed().subscribe({
        error: err => {
          this.errorHandle(err);
        },
        complete: () => {
          // call delete method
          this.deleteSelectedScenarios(industryScenarioIdsToBeDeleted, false);
        }
      });
    } else {
      this.deleteSelectedScenarios(industryScenarioIdsToBeDeleted, false);
    }
  }

  /**
   * Send delete request to the server with list of scenario ids, scenario type,
   * and existsInVehicleScenario (only used in propulsion delete)
   * @param scenarioIds list of ids to be deleted
   * @param existsInVehicleScenario boolean for if the propulsion scenarios need to be removed
   * from the vehicle scenario table before deletion
   */
  deleteSelectedScenarios(scenarioIds: number[], existsInVehicleScenario: boolean): void {
    const scenarioIdsString = scenarioIds.join();
    let titleString = 'Delete';
    if (existsInVehicleScenario) {
      titleString = 'The following propulsion scenario(s) are attached to a vehicle scenario.';
    }
    const dialogRef = this.dialog.open(DeletePopUpComponent, {
      data: {
        title: titleString,
        valueToDelete: scenarioIdsString
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      if (res && res.data.deleteValue) {
        this.showSpinner = true;
        this.manageAllScenariosWithAssociationsService.deleteScenarios(scenarioIds,
          this.currentScenarioType, existsInVehicleScenario).subscribe({
          error: err => {
            this.selectedScenarios = [];
            this.errorHandle(err);
          },
          complete: () => {
            this.selectedScenarios = [];
            this.loadCurrentScenarioType();
            this.snackBar.displaySuccess('Successfully removed scenario(s)', DISMISS_MSG);
            this.showSpinner = false;
          }
        });
      }
    });
  }

  /**
   * Call the appropriate load scenario method by the current scenario type
   */
  loadCurrentScenarioType(): void {
    if (this.currentScenarioType === ALL_SCENARIOS) {
      this.loadAllScenariosWithAssociations();
    }
    if (this.currentScenarioType === VEHICLE) {
      this.loadAllVehicleScenariosWithAssociations();
    }
    if (this.currentScenarioType === PROPULSION) {
      this.loadAllPropulsionScenariosWithAssociations();
    }
    if (this.currentScenarioType === INDUSTRY) {
      this.loadAllIndustryScenariosWithAssociations();
    }
  }

  /**
   * Helper function used to set the export request
   */
  setExportRequest(currentScenarioType: string, tableData: any, columnHeaders: ColumnHeader[]): void {
    const excelData = new Map<string, any>();
    excelData[currentScenarioType] = tableData;
    let fileName = '';
    switch (currentScenarioType) {
      case EXCEL_EXPORT_ALL_SCENARIOS:
        fileName = 'ScenariosAll';
        break;
      case EXCEL_EXPORT_VEH_SCENARIOS:
        fileName = 'ScenariosVeh';
        break;
      case EXCEL_EXPORT_PROP_SCENARIOS:
        fileName = 'ScenariosProp';
        break;
      case EXCEL_EXPORT_IND_SCENARIOS:
        fileName = 'ScenariosInd';
        break;
      default:
        fileName = 'Scenarios';
    }
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    fileName += "_" + currDate;
    this.worksheetExportRequest = this.excelService.createExportRequestBody(fileName,
      excelData,
      [[currentScenarioType, columnHeaders]]);
  }

  /**
   * On tab click, set the current scenario type and export request
   * @param tab current tab
   */
  tabClick(tab): void {
    if (tab.index === 1) {
      this.currentScenarioType = VEHICLE;
      this.setExportRequest(EXCEL_EXPORT_VEH_SCENARIOS, this.vehicleScenarios,
        this.vehicleScenariosHeaders);
    } else if (tab.index === 2) {
      this.currentScenarioType = PROPULSION;
      this.setExportRequest(EXCEL_EXPORT_PROP_SCENARIOS, this.propulsionScenarios,
        this.propulsionScenariosHeaders);
    } else if (tab.index === 3) {
      this.currentScenarioType = INDUSTRY;
      this.setExportRequest(EXCEL_EXPORT_IND_SCENARIOS, this.industryScenarios,
        this.industryScenariosHeaders);
    } else {
      this.currentScenarioType = ALL_SCENARIOS;
      this.setExportRequest(EXCEL_EXPORT_ALL_SCENARIOS, this.allScenariosList,
        this.allScenariosHeaders);
    }
    this.resetTableState();
  }

  /**
   * display message on error.
   * @param error custom error message.
   */
  errorHandle(error: string): void {
    this.showSpinner = false;
    this.snackBar.displayError(error, DISMISS_MSG);
  }
}
